import * as React from 'react'
import AddUserColor from '../../../assets/icons_new/AddUserColor.svg'
import RefreshColor from '../../../assets/icons_new/RefreshColor.svg'
import { useQueryClient } from '@tanstack/react-query'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'
import { CompanyType } from '@/app/constants/companyType'

interface CompanyBasicActionBarProps {
  companyId: string
  onOpenCreateModal: () => void
  /** Total to show as "Ilość firm". Defaults to all companies fetched. */
  totalCount?: number
  /** Optional secondary action — typically "Dodaj firmę z bazy danych". */
  onOpenAddFromDatabase?: () => void
}

const CompanyBasicActionBar: React.FC<CompanyBasicActionBarProps> = ({
  companyId,
  onOpenCreateModal,
  totalCount,
  onOpenAddFromDatabase,
}) => {
  const { t } = useLocalize()
  const { data: companies = [], refetch } = useQueryCompanies({
    includeDeleted: false,
  })
  const queryClient = useQueryClient()

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    await refetch()
  }

  // Mirrors CompaniesTable — service companies are hidden from the list, so
  // they must not inflate the counter either.
  const displayCount =
    totalCount ?? companies.filter((c) => c.type !== CompanyType.SERVICE).length

  return (
    <div className="tw-relative tw-z-[100] tw-w-full">
      <div className="tw-flex tw-w-full tw-justify-between tw-px-4 tw-py-1.5 md:tw-items-center">
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-center">
          <p className="tw-text-sm">
            {t('addCompany.companyCount')}: <span className="tw-font-medium">{displayCount}</span>
          </p>
          <div className="tw-hidden tw-h-5 tw-w-0.5 tw-bg-secondary lg:tw-block"></div>

          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90"
            onClick={onOpenCreateModal}
          >
            <img src={AddUserColor} alt="Add Company Icon" />
            <p className="tw-text-sm tw-font-medium tw-text-primary">
              {t('addCompany.manualCreate')}
            </p>
          </div>

          {onOpenAddFromDatabase && (
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90"
              onClick={onOpenAddFromDatabase}
            >
              <img src={AddUserColor} alt="Add Company From Database Icon" />
              <p className="tw-text-sm tw-font-medium tw-text-primary">
                {t('addCompany.addFromDatabase')}
              </p>
            </div>
          )}
        </div>

        <div
          className="tw-cursor-pointer hover:tw-opacity-90"
          onClick={handleRefresh}
        >
          <img src={RefreshColor} alt="Refresh Icon" />
        </div>
      </div>
    </div>
  )
}

export default CompanyBasicActionBar

